import React, { useRef, useState, useEffect } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, Pressable, Platform, useWindowDimensions, ActivityIndicator } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { colors, typography } from '../../styles/theme';
import BaseModal, { modalStyles, HoverButton } from './BaseModal';
import ModalStickyFooter from '../ui/ModalStickyFooter';
import TemplateThumbnail from '../ui/TemplateThumbnail';
import PackUpsellBanner from '../ui/PackUpsellBanner';
import { useTemplateState } from '../../hooks/useTemplateState';
import { PuzzleCache } from '../../types';

interface SelectPuzzleModalProps {
  visible: boolean;
  onClose: () => void;
  onBack: () => void;
  onSelectPuzzle: (puzzleUrl: string, puzzleId: string) => void;
  onCreateCustom: () => void;
  packTitle: string;
  packId: string | null;
  hasPicloPlay: boolean;
  isPackOwned: boolean;
  priceLabel: string;
  packPriceId?: string;
  onUnlockFreePack: (packId: string) => void;
  puzzleCache: PuzzleCache;
}

export default function SelectPuzzleModal({
  visible,
  onClose,
  onBack,
  onSelectPuzzle,
  onCreateCustom,
  packTitle,
  packId,
  hasPicloPlay,
  isPackOwned,
  priceLabel,
  packPriceId,
  onUnlockFreePack,
  puzzleCache
}: SelectPuzzleModalProps) {
  const flatListRef = useRef<FlatList>(null);
  const { width } = useWindowDimensions();
  const [puzzleIds, setPuzzleIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  useEffect(() => {
    if (visible && packId) {
      setRefreshKey(prev => prev + 1);
      setIsLoading(true);
      fetch(`${process.env.EXPO_PUBLIC_BUCKET_URL}/${packId}/manifest.json`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            let corrected = data;
            if (packId === 'dogs 2') {
              corrected = data.map(id => id.replace(/^dogs-/, 'dogs 2-'));
            } else if (packId === 'cats 2') {
              corrected = data.map(id => id.replace(/^butterflies-/, 'cats 2-'));
            }
            setPuzzleIds(corrected);
          }
        })
        .catch(err => console.error("Failed to fetch manifest:", err))
        .finally(() => setIsLoading(false));
    } else if (!visible) {
      setPuzzleIds([]);
    }
  }, [visible, packId]);

  // No longer using grid layout metrics

  const handlePurchase = () => {
    if (priceLabel === 'Free') {
      if (packId) onUnlockFreePack(packId);
      return;
    }

    if (Platform.OS === 'web') {
      if ((window as any).Paddle?.Checkout) {
        const priceIdToUse = packPriceId || 'pri_01ks79bqva52p3cbwd6m18ajn0';
        (window as any).Paddle.Checkout.open({ items: [{ priceId: priceIdToUse, quantity: 1 }] });
      }
    } else {
      onCreateCustom();
    }
  };

  const handleLockedTap = async () => {
    if (Platform.OS !== 'web') {
      await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    }
    flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
    handlePurchase();
  };

  const renderHeader = () => (
    <View style={styles.listHeader}>
      {!isPackOwned && !hasPicloPlay && (
        <PackUpsellBanner
          priceLabel={priceLabel}
          onPurchase={handlePurchase}
        />
      )}
    </View>
  );

  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      header={(
        <>
          <View style={styles.headerRow}>
            <TouchableOpacity onPress={onBack} style={styles.backButton}>
              <FontAwesome6 name="arrow-left" size={20} color={colors.text} solid />
            </TouchableOpacity>
            <Text style={[modalStyles.headerText, { flex: 1, marginBottom: 0 }]}>{packTitle}</Text>
            <View style={{ width: 44 }} />
          </View>
          <Text style={styles.subtitleText}>Select a puzzle to start playing.</Text>
        </>
      )}
      footer={(
        <ModalStickyFooter>
          <HoverButton
            style={modalStyles.secondaryButton}
            textStyle={modalStyles.secondaryButtonText}
            onPress={onClose}
          >
            Close
          </HoverButton>
        </ModalStickyFooter>
      )}
    >
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          ref={flatListRef}
          data={puzzleIds}
          numColumns={1}
          renderItem={({ item, index }) => (
            <PuzzleCard
              id={item}
              displayIndex={index + 1}
              packId={packId!}
              onSelect={onSelectPuzzle}
              onLocked={handleLockedTap}
              hasPicloPlay={hasPicloPlay}
              isPackOwned={isPackOwned}
              refreshKey={refreshKey}
              isCompleted={puzzleCache[item]?.isCompleted}
            />
          )}
          keyExtractor={(item) => item}
          ListHeaderComponent={renderHeader}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          initialNumToRender={10}
          maxToRenderPerBatch={10}
          windowSize={5}
        />
      )}
    </BaseModal>
  );
}

function PuzzleCard({ id, packId, onSelect, onLocked, hasPicloPlay, isPackOwned, refreshKey, displayIndex, isCompleted }: {
  id: string,
  packId: string,
  onSelect: (url: string, id: string) => void,
  onLocked: () => void,
  hasPicloPlay: boolean,
  isPackOwned: boolean,
  refreshKey: number,
  displayIndex: number,
  isCompleted?: boolean
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const { state: templateState, localSnapshotUri } = useTemplateState(id, !isPackOwned, hasPicloPlay, refreshKey);

  // Cache is the source of truth for completion
  const state = isCompleted ? 'COMPLETED' : templateState;

  const handlePress = () => {
    if (state === 'LOCKED') {
      onLocked();
    } else {
      onSelect(`${process.env.EXPO_PUBLIC_BUCKET_URL}/${packId}/${id}.svg`, id);
    }
  };

  const displayThumbnailUrl = state === 'COMPLETED'
    ? `${process.env.EXPO_PUBLIC_BUCKET_URL}/${packId}/${id}_thumb.webp`
    : (isPackOwned || hasPicloPlay)
      ? `${process.env.EXPO_PUBLIC_BUCKET_URL}/${packId}/${id}_blank_thumb.webp`
      : `${process.env.EXPO_PUBLIC_BUCKET_URL}/${packId}/${id}_thumb.webp`;

  const formatTitle = (rawId: string) => {
    const parts = rawId.split(/[-_]/);
    if (parts.length < 2) return rawId.charAt(0).toUpperCase() + rawId.slice(1);

    let prefix = parts[0].replace(/\s\d+$/, '');

    // Singularize: "Birds" -> "Bird", "Animals" -> "Animal"
    if (prefix.toLowerCase().endsWith('s') && prefix.length > 3) {
      prefix = prefix.slice(0, -1);
    }

    const formattedPrefix = prefix.charAt(0).toUpperCase() + prefix.slice(1);
    return `${formattedPrefix} Puzzle ${displayIndex}`;
  };

  const title = formatTitle(id);

  return (
    <Pressable
      style={({ pressed }) => [
        styles.card,
        Platform.OS === 'web' && isHovered && styles.cardHover,
        pressed && styles.cardPressed
      ]}
      onPress={handlePress}
      // @ts-ignore
      onHoverIn={() => setIsHovered(true)}
      // @ts-ignore
      onHoverOut={() => setIsHovered(false)}
    >
      <TemplateThumbnail
        state={state}
        thumbnailUrl={displayThumbnailUrl}
        localSnapshotUri={localSnapshotUri}
        size={68}
      />
      <View style={styles.cardBody}>
        <Text style={styles.cardTitle}>{title}</Text>
        <Text style={styles.cardStatus}>
          {state === 'COMPLETED' ? 'Completed' : (state === 'LOCKED' ? 'Premium' : 'Unplayed')}
        </Text>
        <View style={styles.cardMetaRow}>
          <FontAwesome6
            name={state === 'LOCKED' ? "lock" : "chevron-right"}
            size={14}
            color="#c1c5ce"
            solid
          />
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  listHeader: {
    width: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    width: '100%',
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  subtitleText: {
    ...typography.body,
    fontSize: 14,
    color: colors.textMuted,
    textAlign: 'center',
    marginBottom: 18,
  },
  scrollContent: {
    paddingHorizontal: 6, // Prevents shadow clipping
    paddingBottom: 20,
    gap: 12,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 7,
    padding: 14,
    boxShadow: `2px 2px 0px ${colors.shadow}`,
    elevation: 3,
  },
  cardHover: {
    boxShadow: `4px 4px 0px ${colors.shadow}`,
    transform: [{ translateY: -1 }],
  },
  cardPressed: {
    opacity: 0.9,
    transform: [{ scale: 0.99 }],
  },
  cardBody: {
    flex: 1,
    marginLeft: 14,
  },
  cardTitle: {
    ...typography.bodyBold,
    fontSize: 16,
    color: colors.text,
    marginBottom: 4,
  },
  cardStatus: {
    ...typography.body,
    fontSize: 13,
    color: colors.textMuted,
    marginBottom: 2,
  },
  cardMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    position: 'absolute',
    right: 0,
    bottom: 2,
  },
  premiumBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderWidth: 2,
    borderColor: colors.primary,
    padding: 16,
    marginBottom: 20,
    boxShadow: `0px 4px 8px ${colors.primary}1A`,
    elevation: 4,
    borderRadius: 10,
  },
  premiumBannerPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.9,
  },
  premiumBannerContent: {
    flex: 1,
    paddingRight: 16,
  },
  premiumBannerTitle: {
    ...typography.bodyBold,
    fontSize: 16,
    color: colors.text,
    marginBottom: 4,
  },
  premiumBannerText: {
    ...typography.body,
    color: colors.textMuted,
    lineHeight: 18,
    fontSize: 13,
  },
  premiumBannerPrice: {
    backgroundColor: colors.primary,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 10,
  },
  premiumPriceText: {
    ...typography.bodyBold,
    color: '#fff',
    fontSize: 14,
  },
});
